import { useState, useEffect } from "react";
import { getUsers, getFoodTrucks, linkUserToFoodTruck, updateUserRole, deleteUser, adminResetUserPassword } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Users,
  Search,
  Link,
  Shield,
  Truck,
  Mail,
  Calendar,
  UserCog,
  Trash2,
  Key,
  Eye,
  EyeOff,
  Loader2,
  UserPlus,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [linkDialog, setLinkDialog] = useState(null);
  const [selectedFoodTruck, setSelectedFoodTruck] = useState("");
  const [roleDialog, setRoleDialog] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Reset password states
  const [resetPasswordDialog, setResetPasswordDialog] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (search) {
      const filtered = users.filter(
        (u) =>
          u.nom.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [search, users]);

  const loadData = async () => {
    try {
      const [usersRes, ftRes] = await Promise.all([getUsers(), getFoodTrucks()]);
      setUsers(usersRes.data);
      setFilteredUsers(usersRes.data);
      setFoodTrucks(ftRes.data);
    } catch (error) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleLinkFoodTruck = async () => {
    if (!linkDialog || !selectedFoodTruck) return;
    try {
      await linkUserToFoodTruck(linkDialog, selectedFoodTruck);
      toast.success("Utilisateur associé au food truck");
      setLinkDialog(null);
      setSelectedFoodTruck("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleUpdateRole = async () => {
    if (!roleDialog || !selectedRole) return;
    try {
      await updateUserRole(roleDialog, selectedRole);
      toast.success("Rôle mis à jour");
      setRoleDialog(null);
      setSelectedRole("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUser(deleteTarget.id);
      toast.success("Utilisateur supprimé");
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordDialog || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    
    setResettingPassword(true);
    try {
      await adminResetUserPassword(resetPasswordDialog.id, newPassword);
      toast.success(`Mot de passe réinitialisé pour ${resetPasswordDialog.email}`);
      setResetPasswordDialog(null);
      setNewPassword("");
      setShowNewPassword(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setResettingPassword(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-primary";
      case "bureau":
        return "bg-highlight";
      case "commercial":
        return "bg-green-600";
      case "benevole":
        return "bg-pink-600";
      default:
        return "bg-secondary";
    }
  };
  
  const getRoleLabel = (role) => {
    switch (role) {
      case "admin":
        return "Administrateur";
      case "bureau":
        return "Bureau";
      case "commercial":
        return "Commercial";
      case "benevole":
        return "Bénévole";
      case "membre":
        return "Membre";
      default:
        return role;
    }
  };

  const getFoodTruckName = (ftId) => {
    const ft = foodTrucks.find((f) => f.id === ftId);
    return ft ? ft.nom_food_truck : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-black text-primary">
          Utilisateurs
        </h1>
        <p className="text-muted-foreground mt-1">
          Gestion des comptes et des rôles
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total utilisateurs</p>
                <p className="text-2xl font-heading font-bold text-secondary">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-highlight/10">
                <Shield className="w-6 h-6 text-highlight" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Admins + Bureau</p>
                <p className="text-2xl font-heading font-bold text-secondary">
                  {users.filter((u) => u.role === "admin" || u.role === "bureau").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100">
                <UserPlus className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Commerciaux</p>
                <p className="text-2xl font-heading font-bold text-green-600">
                  {users.filter((u) => u.role === "commercial").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-accent/10">
                <Link className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Liés à un food truck</p>
                <p className="text-2xl font-heading font-bold text-secondary">
                  {users.filter((u) => u.food_truck_id).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          placeholder="Rechercher un utilisateur..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 border-[#E5E0DC] bg-white"
          data-testid="search-users"
        />
      </div>

      {/* Table */}
      {filteredUsers.length > 0 ? (
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F9F7F5]">
                    <TableHead className="font-semibold text-secondary">Nom</TableHead>
                    <TableHead className="font-semibold text-secondary">Email</TableHead>
                    <TableHead className="font-semibold text-secondary">Rôle</TableHead>
                    <TableHead className="font-semibold text-secondary">Food Truck</TableHead>
                    <TableHead className="font-semibold text-secondary">Inscrit le</TableHead>
                    <TableHead className="font-semibold text-secondary">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => (
                    <TableRow key={u.id} className="hover:bg-[#F9F7F5]" data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.nom}</TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={getRoleColor(u.role)}>
                          {getRoleLabel(u.role)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {u.food_truck_id ? (
                          <span className="flex items-center gap-1 text-status-success">
                            <Truck className="w-4 h-4" />
                            {getFoodTruckName(u.food_truck_id)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Non lié</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {format(parseISO(u.created_at), "dd/MM/yyyy")}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setLinkDialog(u.id);
                              setSelectedFoodTruck(u.food_truck_id || "");
                            }}
                            className="border-[#E5E0DC]"
                            data-testid={`link-user-${u.id}`}
                          >
                            <Truck className="w-4 h-4 mr-1" /> Lier
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setRoleDialog(u.id);
                              setSelectedRole(u.role);
                            }}
                            className="border-[#E5E0DC]"
                            data-testid={`role-user-${u.id}`}
                          >
                            <UserCog className="w-4 h-4 mr-1" /> Rôle
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setResetPasswordDialog(u)}
                            className="border-primary text-primary hover:bg-primary/10"
                            data-testid={`reset-password-${u.id}`}
                            title="Réinitialiser le mot de passe"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteTarget(u)}
                            className="border-status-error text-status-error hover:bg-status-error/10"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden p-4 space-y-3">
              {filteredUsers.map((u) => (
                <div
                  key={u.id}
                  className="p-4 bg-[#F9F7F5] rounded-lg"
                  data-testid={`user-card-${u.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-secondary">{u.nom}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                    </div>
                    <Badge className={getRoleColor(u.role)}>{getRoleLabel(u.role)}</Badge>
                  </div>
                  <div className="text-sm mb-3">
                    {u.food_truck_id ? (
                      <span className="flex items-center gap-1 text-status-success">
                        <Truck className="w-4 h-4" />
                        {getFoodTruckName(u.food_truck_id)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Non lié à un food truck</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setLinkDialog(u.id);
                        setSelectedFoodTruck(u.food_truck_id || "");
                      }}
                      className="flex-1 border-[#E5E0DC]"
                    >
                      <Truck className="w-4 h-4 mr-1" /> Lier
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setRoleDialog(u.id);
                        setSelectedRole(u.role);
                      }}
                      className="flex-1 border-[#E5E0DC]"
                    >
                      <UserCog className="w-4 h-4 mr-1" /> Rôle
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setResetPasswordDialog(u)}
                      className="border-primary text-primary hover:bg-primary/10"
                      title="Réinitialiser le mot de passe"
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl text-secondary mb-2">Aucun utilisateur trouvé</h3>
            <p className="text-muted-foreground">
              {search ? "Modifiez votre recherche" : "Les utilisateurs apparaîtront ici"}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Link Food Truck Dialog */}
      <Dialog open={!!linkDialog} onOpenChange={() => setLinkDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-primary">
              Associer à un food truck
            </DialogTitle>
            <DialogDescription>
              Sélectionnez le food truck à associer à cet utilisateur
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedFoodTruck} onValueChange={setSelectedFoodTruck}>
              <SelectTrigger className="border-[#E5E0DC]" data-testid="select-food-truck">
                <SelectValue placeholder="Sélectionner un food truck" />
              </SelectTrigger>
              <SelectContent>
                {foodTrucks.map((ft) => (
                  <SelectItem key={ft.id} value={ft.id}>
                    {ft.nom_food_truck}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog(null)}>
              Annuler
            </Button>
            <Button
              className="bg-primary hover:bg-primary-hover"
              onClick={handleLinkFoodTruck}
              disabled={!selectedFoodTruck}
              data-testid="confirm-link-btn"
            >
              Associer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Role Dialog */}
      <Dialog open={!!roleDialog} onOpenChange={() => setRoleDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-primary">
              Modifier le rôle
            </DialogTitle>
            <DialogDescription>
              Sélectionnez le nouveau rôle pour cet utilisateur
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="border-[#E5E0DC]" data-testid="select-role">
                <SelectValue placeholder="Sélectionner un rôle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="membre">Membre standard</SelectItem>
                <SelectItem value="benevole">Bénévole</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
                <SelectItem value="bureau">Membre bureau</SelectItem>
                <SelectItem value="admin">Administrateur</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog(null)}>
              Annuler
            </Button>
            <Button
              className="bg-primary hover:bg-primary-hover"
              onClick={handleUpdateRole}
              disabled={!selectedRole}
              data-testid="confirm-role-btn"
            >
              Mettre à jour
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le compte de <strong>{deleteTarget?.nom}</strong> ({deleteTarget?.email}) ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-status-error hover:bg-status-error/90"
              data-testid="confirm-delete-user-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordDialog} onOpenChange={() => {
        setResetPasswordDialog(null);
        setNewPassword("");
        setShowNewPassword(false);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary flex items-center gap-2">
              <Key className="w-5 h-5" />
              Réinitialiser le mot de passe
            </DialogTitle>
            <DialogDescription>
              Définir un nouveau mot de passe pour <strong>{resetPasswordDialog?.nom}</strong> ({resetPasswordDialog?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-secondary font-semibold">Nouveau mot de passe</Label>
              <div className="relative mt-1">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="border-[#E5E0DC] pr-10"
                  placeholder="Minimum 6 caractères"
                  data-testid="admin-reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-secondary"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                L'utilisateur devra utiliser ce mot de passe pour se connecter
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResetPasswordDialog(null);
                setNewPassword("");
                setShowNewPassword(false);
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resettingPassword || !newPassword || newPassword.length < 6}
              className="bg-primary hover:bg-primary-hover"
              data-testid="confirm-reset-password-btn"
            >
              {resettingPassword ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Key className="w-4 h-4 mr-2" />
              )}
              Réinitialiser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
